import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { triangle } from 'polished';
import { ApButtonSecondary, ApModal } from '@alixpartners/ui-components';

import PageContent from 'components/PageContent';
import {
	black,
	brandColors,
	grey30,
	grey150,
	grey200,
	grey400,
	chartDarkGray,
	greenLight3,
	chartJaffaOrange,
} from 'styles/colors';

export const Wrapper = PageContent.extend``;

export const StyledNavLink = styled(NavLink)`
	display: inline-block;
	padding: 15px 8px;
	margin-right: 5px;
	position: relative;
	text-transform: uppercase;
	font-size: 13px;
	font-weight: 500;
	letter-spacing: 0.3px;
	line-height: 13px;

	&.active {
		color: ${brandColors.green};
		border-bottom: 2px solid ${brandColors.green};
	}

	${({ disabled }) =>
		disabled &&
		`
			color: ${grey200};
	`};
`;

export const NotificationWrapper = styled.div`
	border-top: 3px solid
		${({ isSuccess }) => (isSuccess ? greenLight3 : chartJaffaOrange)};
	margin: 21px 0 24px 0;
`;

export const ChangesBlock = styled.div`
	padding-bottom: 24px;
`;

export const ChangesNoItemsMessage = styled.div`
	padding: 16px;

	svg {
		margin-right: 8px;
		margin-left: 56px;
		position: relative;
		bottom: -5px;
	}
`;

export const ChangesBlockTitle = styled.div`
	height: 24px;
	margin-top: 12px;
	padding: 12px 20px 32px 74px;
	font-size: 16px;
	font-weight: bold;
	letter-spacing: 0.3px;
	line-height: 24px;
	text-transform: uppercase;
	position: relative;
	cursor: pointer;

	&::before {
		content: '';
		position: absolute;
		left: 42px;
		top: ${props => (props.open ? '20px' : '18px')};
		${props =>
			triangle(
				props.open
					? {
							pointingDirection: 'bottom',
							foregroundColor: grey400,
							width: 10,
							height: 6,
					  }
					: {
							pointingDirection: 'right',
							foregroundColor: grey400,
							width: 6,
							height: 10,
					  }
			)};
	}
`;

export const ChangesList = styled.ul`
	margin-top: 8px;
	margin-bottom: 0;
	padding: 0 20px 32px 68px;
`;

export const ChangesListCell = styled.span`
	width: 134px;
	min-width: 134px;
	padding-left: 30px;
	display: inline-block;
	&:first-child {
		padding-left: 8px;
		text-transform: uppercase;
		color: ${chartDarkGray};
		font-size: 13px;
		font-weight: 500;
	}
	&:nth-child(2) {
		width: 274px;
		min-width: 274px;
	}
	&:last-child {
		width: auto;
		min-width: auto;
	}
`;

export const ChangesListRow = styled.li`
	display: flex;
	line-height: 32px;
	border-bottom: 1px solid ${grey150};
	cursor: pointer;
	background: ${({ isActive }) => (isActive ? grey30 : 'none')};
`;

export const ChangesListHeader = styled(ChangesListRow)`
	text-transform: uppercase;
	color: ${chartDarkGray};
	font-size: 11px;
	letter-spacing: 0.8px;

	height: 21px;
	line-height: 13px;

	${ChangesListCell} {
		font-weight: inherit;
		font-size: inherit;
	}
`;

export const ActiveList = styled.ul`
	background: ${grey30};
	padding-left: 163px;

	&:last-child {
		border-bottom: 1px solid ${grey150};
	}
`;

export const ActiveRow = styled.li`
	display: flex;
	line-height: 40px;
	margin: 0 24px 0 0;
	border-bottom: 1px solid ${grey150};
	background: ${({ isActive }) => (isActive ? grey30 : 'none')};
	&:last-child {
		border-bottom: none;
	}
`;

export const ActiveRowCell = styled.span`
	width: calc(25% - 8px);
	padding-left: 30px;
	display: inline-block;
	margin-top: 12px;

	&:first-child {
		padding-left: 0;
	}
`;

export const ActiveRowCellTitle = styled.div`
	line-height: 13px;
	color: ${chartDarkGray};
	font-size: 11px;
	letter-spacing: 0;
`;

export const ActiveRowCellValue = styled.div`
	line-height: 19px;
	color: ${black};
	font-size: 13px;
	letter-spacing: 0.3px;
	padding-top: 4px;
	padding-bottom: 4px;
`;

export const ActiveListDescription = styled.div`
	font-size: 13px;
	letter-spacing: 0.3px;
	line-height: 19px;
	padding-top: 8px;
	padding-bottom: 4px;
`;

export const StyledModal = styled(ApModal)`
	.ap-modal-content {
		max-width: 480px;
	}

	.ap-modal-header {
		border-bottom: none;
		padding: 27px 32px 8px 32px;
	}

	.ap-modal-footer {
		padding: 13px 32px 12px 32px;
	}

	.ap-modal-body {
		padding: 16px 32px 40px 32px;
	}
`;

export const ModalBody = styled.div`
	color: ${black};
	font-size: 16px;
	letter-spacing: 0.3px;
	line-height: 24px;
`;

export const StyledButtonSecondary = styled(ApButtonSecondary)`
	margin-right: 32px;
`;

export const ModalHeader = styled.div`
	color: ${black};
	font-size: 23px;
	font-weight: 500;
	letter-spacing: 0.3px;
	line-height: 31px;
`;

export const SectionTitle = styled.div`
	color: ${chartDarkGray};
	font-size: 19px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: 27px;
	padding-left: 74px;
	padding-top: 8px;
`;

export const AppliedDateInfoText = styled.div`
	color: ${chartDarkGray};
	font-size: 13px;
	letter-spacing: 0.3px;
	line-height: 19px;
`;

export const PreviousUpgradesSection = styled.div`
	border-top: 3px solid
		${({ isSuccess }) => (isSuccess ? greenLight3 : chartJaffaOrange)};

	${ChangesBlock} {
		padding-bottom: 0;
	}

	${ChangesBlockTitle} {
		font-size: 16px;
		text-transform: inherit;
		margin-top: 0;
		color: black;

		font-weight: bold;
		letter-spacing: 0.3px;
		line-height: 24px;
	}

	${ChangesList} {
		padding-bottom: 0;
		margin-bottom: 16px;
	}
`;

export const PreviousUpgradesSectionTitle = styled.div`
	margin: 48px 8px 6px 34px;
	font-size: 19px;
	font-weight: 600;
`;

export const LoaderWrapper = styled.div`
	height: 160px;
	display: flex;
`;
